import { getOrgConfig, defaultAmountField } from "../../../Common/Hooks/OrgConfigHooks";
import { useState, useEffect } from 'react';
import Breadcrumb from '../../../Common/Breadcrumb';
import { APIClient } from "../../../helpers/api_helper";
import { Button, Col, Row, Form, Switch, Tooltip, Breadcrumb as AntBreadcrumb } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import BasePieChartWithTable from '../../../Common/BasePieChartWithTable';
import AmountFieldSelect from '../../../Common/AmountFieldSelect';
import { useSelector, useDispatch } from "react-redux";
import utils from '../../../Common/utils';
import { setInitialFilterConf, updateFilterData } from '../../../slices/filters/thunk'
import { Link } from 'react-router-dom';
import ShowIntentsBtn from '../../../Common/ShowIntentsBtn';
import InsightsChartCard from '../../../Common/InsightsChartCard';


const api = new APIClient();


const InsightsExpenses = () => {
  document.title = "Odhodki" + process.env.REACT_APP_PAGE_TITLE;
  const dispatch = useDispatch();

  const filterdata = useSelector((state) => state.Filterdata.data)
  console.log('EXPENSES FILTER DATA', filterdata)
  const [expensesByCompanyData, setExpensesByCompanyData] = useState([]);
  const [expensesByTagData, setExpensesByTagData] = useState([]);
  const [expensesByProjectData, setExpensesByProjectData] = useState([]);
  const [expensesByCategoryData, setExpensesByCategoryData] = useState([]);
  const [loadingByTag, setLoadingByTag] = useState(false);
  const [loadingByCompany, setLoadingByCompany] = useState(false);
  const [loadingByProject, setLoadingByProject] = useState(false);
  const [loadingByCategory, setLoadingByCategory] = useState(false);
  // Breadcrumb of the tag the "Po oznaki" chart is drilled into (empty = top level).
  const [tagPath, setTagPath] = useState([]);
  // Flat mode: show the actual child tags as their own slices instead of rolling
  // each subtree up into its root. Composes with drilling -- it flattens
  // whatever scope is currently in view.
  const [flatTags, setFlatTags] = useState(false);
  // Tags opened individually: each is replaced by its direct children while the
  // rest of the chart stays rolled up. Independent of `flatTags`, which opens
  // everything at once.
  const [expandedTags, setExpandedTags] = useState([]);
  // Which panel, if any, is widened to the full page. Only one at a time.
  const [expandedChart, setExpandedChart] = useState(null);

  const toggleTagExpanded = (id) => setExpandedTags(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const tagViewIsCustomised = tagPath.length > 0 || expandedTags.length > 0 || flatTags;

  // "Show transactions" for the tag card has to follow the drill, or it silently
  // lists everything while the chart beside it shows one category. `tags_filters`
  // expands to descendants server-side (IntentsFilter._expand_tags), so pointing
  // it at the drilled tag matches the chart's scope exactly. Expansions are only
  // re-bucketing, so they do not affect which transactions are in scope.
  const currentTag = tagPath.length ? tagPath[tagPath.length - 1] : null;
  const tagIntentsHash = currentTag
    ? utils.getUpdatedURL({ tags_filters: [currentTag.id] })
    : utils.getUpdatedURL();
  const resetTagView = () => {
    setTagPath([]);
    setExpandedTags([]);
    setFlatTags(false);
  };

  const loadByTag = () => {
    const tagParent = tagPath.length ? tagPath[tagPath.length - 1].id : undefined;
    setLoadingByTag(true);
    api.get('/api/insights/expenses_by_tag/', {
      ...utils.toFilterParams(filterdata),
      tag_parent: tagParent,
      tag_flat: flatTags ? 1 : undefined,
      tag_expanded: expandedTags.length ? expandedTags.join(',') : undefined,
    })
      .then(resp => setExpensesByTagData(resp))
      .finally(() => setLoadingByTag(false));
  }

  const loadInsightsData = () => {
    setLoadingByCompany(true);
    api.get('/api/insights/expenses_by_company/', utils.toFilterParams(filterdata))
      .then(resp => setExpensesByCompanyData(resp))
      .finally(() => setLoadingByCompany(false));
    setLoadingByProject(true);
    api.get('/api/insights/expenses_by_project/', utils.toFilterParams(filterdata))
      .then(resp => setExpensesByProjectData(resp))
      .finally(() => setLoadingByProject(false));
    setLoadingByCategory(true);
    api.get('/api/insights/expenses_by_category/', utils.toFilterParams(filterdata))
      .then(resp => setExpensesByCategoryData(resp))
      .finally(() => setLoadingByCategory(false));
  }

  useEffect(() => {
    dispatch(setInitialFilterConf({
      data: { amount_field: defaultAmountField(getOrgConfig()) },
      hidden: ['intent_type', 'intent_checkboxes_group'],
      // The checkbox group is hidden wholesale, but a paid filter is needed here:
      // without it these totals mix realised spending with what is only planned.
      shown: ['is_paid'],
      disabled: ['search'],
      masterSearch: true,
    }))
  }, []);

  // Drilling resets when the global filters change.
  useEffect(() => {
    setTagPath([]);
    setExpandedTags([]);
    loadInsightsData()
  }, [filterdata]);

  useEffect(() => {
    setExpandedTags([]);
  }, [tagPath]);

  useEffect(() => {
    loadByTag()
  }, [filterdata, tagPath, flatTags, expandedTags]);

  const crumbLink = (label, onClick) => (
    <span style={{ cursor: 'pointer' }} onClick={onClick}>{label}</span>
  );
  const tagBreadcrumb = (
    <AntBreadcrumb
      items={[
        { title: crumbLink('Vse oznake', () => setTagPath([])) },
        ...tagPath.map((t, i) => ({
          title: i === tagPath.length - 1
            ? t.title
            : crumbLink(t.title, () => setTagPath(tagPath.slice(0, i + 1)))
        })),
      ]}
    />
  );

  // Breadcrumb (only once drilled in) on the left, reset and flat-mode toggle on
  // the right. Stepping up exactly one level is the up arrow above the table;
  // the breadcrumb is here for jumping several at once.
  const tagChartHeader = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      flexWrap: 'wrap', marginBottom: 8, minHeight: 24,
    }}>
      {tagPath.length > 0 && tagBreadcrumb}
      {tagViewIsCustomised && (
        <Tooltip title="Počisti razčlenitev in se vrni na privzeti pogled">
          <Button size="small" icon={<ReloadOutlined />} onClick={resetTagView}>
            Ponastavi
          </Button>
        </Tooltip>
      )}
      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Tooltip title="Namesto seštevka po korenski oznaki prikaži posamezne podoznake kot svoje deleže.">
          <span style={{ fontSize: 12 }}>Razčleni vse</span>
        </Tooltip>
        <Switch size="small" checked={flatTags} onChange={setFlatTags} />
      </span>
    </div>
  );


  return (
    <>
      <Breadcrumb parentTitle="Vpogledi" pageTitle="Odhodki" />
      <>
        <Col xs={24} className="gutter-row">

            <Row>
              <Col span={4}>
                <Form.Item label="Znesek">
                  <AmountFieldSelect
                    value={filterdata.amount_field || defaultAmountField(getOrgConfig())}
                    onChange={val => dispatch(updateFilterData({ amount_field: val }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>

              <InsightsChartCard
                chartKey="tag"
                title="Po oznaki"
                expandedKey={expandedChart}
                onToggleExpanded={setExpandedChart}
                extra={<Link to={{pathname: "/intents", hash: tagIntentsHash}}><ShowIntentsBtn size='large' /></Link>}
              >
                  <BasePieChartWithTable
                    data={expensesByTagData}
                    loading={loadingByTag}
                    aggColTitle={'Oznaka'}
                    filterName={'tags_filters'}
                    moneyType={'EXPENSE'}
                    headerExtra={tagChartHeader}
                    onDrillDown={row => setTagPath([...tagPath, { id: row.id, title: row.title }])}
                    onToggleExpand={id => toggleTagExpanded(id)}
                    currentTitle={currentTag ? currentTag.title : null}
                    onGoUp={tagPath.length ? () => setTagPath(tagPath.slice(0, -1)) : undefined}
                  />
              </InsightsChartCard>

              <InsightsChartCard
                chartKey="company"
                title="Po podjetju"
                expandedKey={expandedChart}
                onToggleExpanded={setExpandedChart}
                extra={<Link to={{pathname: "/intents", hash: utils.getUpdatedURL()}}><ShowIntentsBtn size='large' /></Link>}
              >
                  <BasePieChartWithTable
                    data={expensesByCompanyData}
                    loading={loadingByCompany}
                    aggColTitle={'Podjetje'}
                    filterName={'companies'}
                    moneyType={'EXPENSE'}
                  />
              </InsightsChartCard>

              <InsightsChartCard
                chartKey="project"
                title="Po projektu"
                expandedKey={expandedChart}
                onToggleExpanded={setExpandedChart}
                extra={<Link to={{pathname: "/intents", hash: utils.getUpdatedURL()}}><ShowIntentsBtn size='large' /></Link>}
              >
                  <BasePieChartWithTable
                    data={expensesByProjectData}
                    loading={loadingByProject}
                    aggColTitle={'Projekt'}
                    filterName={'projects'}
                    moneyType={'EXPENSE'}
                  />
              </InsightsChartCard>

              <InsightsChartCard
                chartKey="category"
                title="Po kategoriji"
                expandedKey={expandedChart}
                onToggleExpanded={setExpandedChart}
                extra={<Link to={{pathname: "/intents", hash: utils.getUpdatedURL()}}><ShowIntentsBtn size='large' /></Link>}
              >
                  <BasePieChartWithTable
                    data={expensesByCategoryData}
                    loading={loadingByCategory}
                    aggColTitle={'Kategorija'}
                    filterName={'category'}
                    moneyType={'EXPENSE'}
                  />
              </InsightsChartCard>
            </Row>

        </Col>
      </>
    </>
  )
}

export default InsightsExpenses;
